import { api } from "api";
import { handleActions } from "redux-actions";
import { createReducer } from "../baseReducer/baseReducer";
import { NotificationManager } from "react-notifications";
import { push } from "react-router-redux";
import { change } from "redux-form";
import Swal from 'sweetalert2';

const baseReducer = createReducer("users", "user", "userForm", "/users");

// ------------------------------------
// Constants
// ------------------------------------
const SET_OPTION_STATION_USER = "SET_OPTION_STATION_USER";
const SET_FIREFIGHTERS = "SET_FIREFIGHTERS";
const SET_PILOT = "SET_PILOT";
const SET_OPTION_ROLE_USER = "SET_OPTION_ROLE_USER";
const SET_OPTION_FIREFIGHTER_TYPE = "SET_OPTION_FIREFIGHTER_TYPE";
const SET_AGE_MIN = "SET_AGE_MIN";
const SET_AGE_MAX = "SET_AGE_MAX";
const SET_GENDER = "SET_GENDER";
const SET_OPTION_STATUS_USER = "SET_OPTION_STATUS_USER";

// ------------------------------------
// Pure Actions
// ------------------------------------

const setOptionsStation = (station) => ({
    type: SET_OPTION_STATION_USER,
    station,
});

const setFirefighters = (firefighters) => ({
    type: SET_FIREFIGHTERS,
    firefighters,
});

const setPilot = (pilot) => ({
    type: SET_PILOT,
    pilot,
});

const setOptionsRole = (role) => ({
    type: SET_OPTION_ROLE_USER,
    role,
});

const setOptionsFirefighterType = (firefighterType) => ({
    type: SET_OPTION_FIREFIGHTER_TYPE,
    firefighterType,
});

const setAgeMin = (ageMin) => ({
    type: SET_AGE_MIN,
    ageMin,
});

const setAgeMax = (ageMax) => ({
    type: SET_AGE_MAX,
    ageMax,
});

const setGender = (gender) => ({
    type: SET_GENDER,
    gender,
});

const setOptionsStatus = (status) => ({
    type: SET_OPTION_STATUS_USER,
    status,
});

const setExcelLoading = (payload) => ({
    type: 'SET_EXCEL_LOADING',
    payload,
});

const setExcelProgress = (payload) => ({
    type: 'SET_EXCEL_PROGRESS',
    payload,
});

// Normalize values that come from <select> with null/undefined options
// React renders null as the string "null" in <option value={null}>
const normalizeFilterValue = (value) => {
    if (value === null || value === undefined || value === 'null' || value === 'undefined' || value === '') {
        return '';
    }
    return value;
};

const customUserList =
    (page = 1) =>
        (dispatch, getStore) => {
            const resource = getStore()["users"];
            let station = resource.station;
            let role = resource.role;
            let firefighterType = resource.firefighterType;
            let ageMin = resource.ageMin;
            let ageMax = resource.ageMax;

            let gender = resource.gender;
            let status = resource.status;

            const params = { page };
            params.ordering = resource.ordering;
            params.search = resource.search;

            params.station_id = station || "";
            params.role = role || "";
            params.firefighter_type = firefighterType || "";

            if (ageMin) params.age_min = ageMin;
            if (ageMax) params.age_max = ageMax;

            params.gender = gender || "";
            params.status = status || "";

            dispatch(baseReducer.actions.setLoader(true));
            api.get("user", params)
                .then((response) => {
                    dispatch(baseReducer.actions.setData(response));
                    dispatch(baseReducer.actions.setPage(page));
                })
                .catch((err) => {
                    console.log("Error:", err);
                })
                .finally(() => {
                    dispatch(baseReducer.actions.setLoader(false));
                });
        };

const downloadExcel = () => (dispatch, getStore) => {
    const resource = getStore()["users"];
    let station = resource.station;
    let role = resource.role;
    let firefighterType = resource.firefighterType;
    let ageMin = resource.ageMin;
    let ageMax = resource.ageMax;
    let gender = resource.gender;

    const params = {};
    params.ordering = resource.ordering;
    params.search = resource.search;
    params.station_id = station;
    params.role = role;
    params.firefighter_type = firefighterType;
    if (ageMin) params.age_min = ageMin;
    if (ageMax) params.age_max = ageMax;

    if (gender) params.gender = gender;
    // Note: status is not explicitly used in downloadExcel in the original code, 
    // but if we want Excel to match list, we should probably add it. 
    // However, for now I will stick to what is strictly needed for the list or follow the pattern.
    // The previous implementation of downloadExcel manually constructs params.
    let status = resource.status;
    if (status) params.status = status;

    dispatch(setExcelLoading(true));
    dispatch(setExcelProgress(0));

    api.getWithProgress("user/download_excel", params, (percent) => {
        dispatch(setExcelProgress(percent));
    })
        .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Listado_Usuarios.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }).catch(() => {
            NotificationManager.error('Error al descargar', 'ERROR');
        }).finally(() => {
            dispatch(setExcelLoading(false));
            dispatch(setExcelProgress(0));
        });
};

const updateDischarge = (id, data = {}, attachments = []) => (dispatch, getStore) => {
    dispatch(baseReducer.actions.setLoader(true));
    api.putAttachments(`/job_management/${id}/updateDischarge`, data, attachments).then((response) => {
        NotificationManager.success('Datos actualizados', 'Exito', 1000);
        dispatch(push('/human_resources/unsubscribe_discharge'));
    }).catch((errr) => {
        console.log('THE ERROR IS: ', errr)
        NotificationManager.error('Error', 'ERROR', 0);
    }).finally(() => {
        dispatch(baseReducer.actions.setLoader(false));
    });
};

const updateUnsubscribe = (id, data = {}, attachments = []) => (dispatch, getStore) => {
    dispatch(baseReducer.actions.setLoader(true));
    api.putAttachments(`/job_management/${id}/updateUnsubscribe`, data, attachments).then((response) => {
        NotificationManager.success('Datos actualizados', 'Exito', 1000);
        dispatch(push('/human_resources/unsubscribe_discharge'));
    }).catch((errr) => {
        console.log('THE ERROR IS: ', errr)
        NotificationManager.error(errr.detail ? errr.detail : 'Error', 'ERROR', 0);
    }).finally(() => {
        dispatch(baseReducer.actions.setLoader(false));
    });
};

const listarBomberos = (stations) => (dispatch, getStore) => {
    const params = { stations };
    dispatch(baseReducer.actions.setLoader(true));
    api.get("user/get_firefighters", params)
        .then((response) => {
            dispatch(setFirefighters(response));
        })
        .catch(() => { })
        .finally(() => {
            dispatch(baseReducer.actions.setLoader(false));
        });

    api.get("user/get_pilot", params)
        .then((response) => {
            dispatch(setPilot(response));
        })
        .catch(() => { })
        .finally(() => {
            dispatch(baseReducer.actions.setLoader(false));
        });

};

const limpiarBomberos = () => (dispatch) => {
    dispatch(setFirefighters([]));
    dispatch(setPilot([]));
}

const validateUserCreated = (id) => (dispatch) => {
    api.post('user/validate_user_created', { id })
        .then((response) => {
            NotificationManager.success('Usuario validado', 'Exito', 1000);
            dispatch(push('/users'));
        })
        .catch((err) => { console.log('error', err) })
        .finally(() => {
            console.log('final')
        });
};

const filterByStation = (value) => (dispatch, getStore) => {
    if (value && typeof value === 'object' && value.hasOwnProperty('value')) {
        value = value.value;
    }
    value = normalizeFilterValue(value);
    dispatch(setOptionsStation(value))
    dispatch(customUserList())
}

const filterByRole = (value) => (dispatch, getStore) => {
    value = normalizeFilterValue(value);
    dispatch(setOptionsRole(value));
    dispatch(customUserList());
};

const filterByFirefighterType = (value) => (dispatch, getStore) => {
    value = normalizeFilterValue(value);
    dispatch(setOptionsFirefighterType(value));
    dispatch(customUserList());
};

const filterByAgeMin = (value) => (dispatch) => {
    dispatch(setAgeMin(normalizeFilterValue(value)));
    dispatch(customUserList());
};

const filterByAgeMax = (value) => (dispatch) => {
    dispatch(setAgeMax(normalizeFilterValue(value)));
    dispatch(customUserList());
};

const filterByGender = (value) => (dispatch) => {
    dispatch(setGender(normalizeFilterValue(value)));
    dispatch(customUserList());
};

const filterByStatus = (value) => (dispatch) => {
    dispatch(setOptionsStatus(normalizeFilterValue(value)));
    dispatch(customUserList());
};

const updateAttachment = (id, data = {}, attachments = []) => (dispatch, getStore) => {
    dispatch(baseReducer.actions.setLoader(true));
    api.putAttachments(`${'user'}/${id}`, data, attachments).then((response) => {
        NotificationManager.success('Datos actualizados', 'Exito', 1000);
        dispatch(push('/users'));
    }).catch((errr) => {
        console.log('THE ERROR IS: ', errr)
        NotificationManager.error('Credenciales incorrectas, vuelva a intentar', 'ERROR', 0);
    }).finally(() => {
        dispatch(baseReducer.actions.setLoader(false));
    });
};


const createAttachment = (data = {}, attachments = []) => (dispatch, getStore) => {
    dispatch(baseReducer.actions.setLoader(true));
    api.postAttachments(`user`, data, attachments).then((response) => {
        let password = null;
        if (response && response.generated_password) {
            password = response.generated_password;
        } else if (response && response.body && response.body.generated_password) {
            password = response.body.generated_password;
        } else if (response && response.data && response.data.generated_password) {
            password = response.data.generated_password;
        }

        if (password) {
            Swal.fire({
                title: 'Usuario Creado',
                html: `Contraseña generada: <b>${password}</b><br/>Copie esta contraseña y entréguela al usuario.<br/><small>Esta contraseña es temporal y deberá cambiarse al iniciar sesión.</small>`,
                icon: 'success',
                allowOutsideClick: false,
                confirmButtonText: 'Aceptar'
            }).then(() => {
                dispatch(push('/users'));
            });
        } else {
            Swal.fire({
                title: 'Debugging',
                html: `El usuario con foto se creó pero el backend no devolvió la contraseña. Estructura recibida:<br/><br/><code>${JSON.stringify(response).substring(0, 300)}</code>`,
                icon: 'warning',
                allowOutsideClick: false,
                confirmButtonText: 'Aceptar'
            }).then(() => {
                dispatch(push('/users'));
            });
        }
    }).catch((err) => {
        let errorMsg = '';
        if (err && (err.no_dpi || err.username)) {
            errorMsg += 'Ya existe un usuario registrado con este N° DPI.<br/>';
        }
        if (err && err.email) {
            errorMsg += 'Ya existe un usuario registrado con este correo electrónico.<br/>';
        }

        if (errorMsg) {
            Swal.fire({
                title: 'Error de validación',
                html: errorMsg,
                icon: 'error',
                confirmButtonText: 'Entendido'
            });
        } else {
            NotificationManager.error('Error en el formulario o credenciales, vuelva a intentar', 'ERROR', 0);
        }
    }).finally(() => {
        dispatch(baseReducer.actions.setLoader(false));
    });
};

const crear = (data) => (dispatch) => {
    dispatch(baseReducer.actions.setLoader(true));
    api.post("user", data)
        .then((response) => {
            let password = null;
            if (response && response.generated_password) {
                password = response.generated_password;
            } else if (response && response.body && response.body.generated_password) {
                password = response.body.generated_password;
            } else if (response && response.data && response.data.generated_password) {
                password = response.data.generated_password;
            }

            if (password) {
                Swal.fire({
                    title: 'Usuario Creado',
                    html: `Contraseña generada: <b>${password}</b><br/>Copie esta contraseña y entréguela al usuario.<br/><small>Esta contraseña es temporal y deberá cambiarse al iniciar sesión.</small>`,
                    icon: 'success',
                    allowOutsideClick: false,
                    confirmButtonText: 'Aceptar'
                }).then(() => {
                    dispatch(push('/users'));
                });
            } else {
                Swal.fire({
                    title: 'Debugging',
                    html: `El usuario se creó pero el backend no devolvió la contraseña. Estructura recibida:<br/><br/><code>${JSON.stringify(response).substring(0, 300)}</code>`,
                    icon: 'warning',
                    allowOutsideClick: false,
                    confirmButtonText: 'Aceptar'
                }).then(() => {
                    dispatch(push('/users'));
                });
            }
        })
        .catch((err) => {
            let errorMsg = '';
            if (err && (err.no_dpi || err.username)) {
                errorMsg += 'Ya existe un usuario registrado con este N° DPI.<br/>';
            }
            if (err && err.email) {
                errorMsg += 'Ya existe un usuario registrado con este correo electrónico.<br/>';
            }

            if (errorMsg) {
                Swal.fire({
                    title: 'Error de validación',
                    html: errorMsg,
                    icon: 'error',
                    confirmButtonText: 'Entendido'
                });
            } else {
                NotificationManager.error('Error en el formulario o credenciales, vuelva a intentar', 'ERROR', 0);
            }
        })
        .finally(() => {
            dispatch(baseReducer.actions.setLoader(false));
        });
};

const changeValueSelectUserStation = () => (dispatch, getStore) => {
    const resource = getStore()["users"];
    dispatch(change("ListUsers", "station", resource.station));
};

const searchCustomChange = (search) => (dispatch) => {
    dispatch(baseReducer.actions.setSearch(search));
    dispatch(customUserList());
};

export const actions = {
    crear,
    filterByRole,
    filterByFirefighterType,
    filterByAgeMin,
    filterByAgeMax,
    filterByGender,
    downloadExcel,
    customUserList,
    filterByStation,
    filterByStatus,
    updateAttachment,
    createAttachment,
    searchCustomChange,
    validateUserCreated,
    listarBomberos,
    limpiarBomberos,
    updateDischarge,
    updateUnsubscribe,
    ...baseReducer.actions,
    crear,
    changeValueSelectUserStation,
};

export const reducers = {
    ...baseReducer.reducers,
    [SET_OPTION_STATION_USER]: (state, { station }) => {
        return {
            ...state,
            station,
        };
    },
    [SET_FIREFIGHTERS]: (state, { firefighters }) => {
        return {
            ...state,
            firefighters,
        };
    },

    [SET_PILOT]: (state, { pilot }) => {
        return {
            ...state,
            pilot,
        };
    },

    [SET_OPTION_ROLE_USER]: (state, { role }) => {
        return {
            ...state,
            role,
        };
    },

    [SET_OPTION_FIREFIGHTER_TYPE]: (state, { firefighterType }) => {
        return {
            ...state,
            firefighterType,
        };
    },
    [SET_AGE_MIN]: (state, { ageMin }) => ({ ...state, ageMin }),
    [SET_AGE_MAX]: (state, { ageMax }) => ({ ...state, ageMax }),
    [SET_GENDER]: (state, { gender }) => ({ ...state, gender }),
    [SET_OPTION_STATUS_USER]: (state, { status }) => ({ ...state, status }),
    ['SET_EXCEL_LOADING']: (state, { payload }) => ({ ...state, excelLoading: payload }),
    ['SET_EXCEL_PROGRESS']: (state, { payload }) => ({ ...state, excelProgress: payload }),
};

export const initialState = {
    station: "",
    firefighters: [],
    pilot: [],
    role: "",
    firefighterType: "",
    ageMin: "",
    ageMax: "",
    gender: "",
    status: "",
    excelLoading: false,

    excelProgress: 0,
    ...baseReducer.initialState,
};

export default handleActions(reducers, initialState);
